import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getRefunds } from '../api/refundApi';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { getStatusClass, getStatusStyle } from '../utils/statusUtils';
import { 
  DollarSign, 
  RefreshCcw, 
  CreditCard
} from 'lucide-react';

const DashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRefunds: 0,
    totalAmount: 0,
  });
  const [methodStats, setMethodStats] = useState([]);

  // İade verilerini yükle
  useEffect(() => {
    const fetchRefunds = async () => {
      try {
        // API çağrısı ve veri alımı
        const data = await getRefunds();
        console.log("Dashboard API yanıtı:", data);
        
        // Data'nın array olup olmadığını kontrol et
        const refundsArray = Array.isArray(data) ? data : [];
        
        setRefunds(refundsArray);
        calculateStats(refundsArray);
        calculateMethodStats(refundsArray);
      } catch (error) {
        toast.error('İade verileri yüklenirken bir hata oluştu');
        console.error('İade verileri yüklenirken hata:', error);
        // Hata durumunda boş array'ler ile çalış
        setRefunds([]);
        calculateStats([]);
        calculateMethodStats([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRefunds();
  }, []);

  // İstatistikleri hesapla
  const calculateStats = (refundData) => {
    if (!Array.isArray(refundData)) {
      console.error("calculateStats için geçersiz veri:", refundData);
      return;
    }
    
    const totalRefunds = refundData.length;
    
    let totalAmount = 0;
    
    refundData.forEach(refund => {
      // Toplam tutar
      totalAmount += parseFloat(refund.amount || 0);
    });
    
    setStats({
      totalRefunds,
      totalAmount: totalAmount.toFixed(2),
    });
  };
  
  // Ödeme yöntemlerine göre istatistikleri hesapla
  const calculateMethodStats = (refundData) => {
    if (!Array.isArray(refundData)) {
      console.error("calculateMethodStats için geçersiz veri:", refundData);
      return;
    }
    
    // Ödeme yöntemlerine göre grupla
    const methodGroups = {};
    
    refundData.forEach(refund => {
      const methodId = refund.payment_method_id;
      const methodName = refund.payment_method_name || 'Bilinmeyen Yöntem';
      const amount = parseFloat(refund.amount || 0);
      const statusName = refund.status_name || 'Bilinmeyen Durum';
      const statusColor = refund.color; // Renk bilgisi
      
      // Yöntemi ekle veya güncelle
      if (!methodGroups[methodId]) {
        methodGroups[methodId] = {
          id: methodId,
          name: methodName,
          totalAmount: 0,
          totalCount: 0,
          statusCounts: {},
          statusColors: {} // Renk bilgilerini saklayacak nesne
        };
      }
      
      // İstatistikleri güncelle
      methodGroups[methodId].totalAmount += amount;
      methodGroups[methodId].totalCount += 1;
      
      // Durum sayılarını güncelle
      if (!methodGroups[methodId].statusCounts[statusName]) {
        methodGroups[methodId].statusCounts[statusName] = 0;
        methodGroups[methodId].statusColors[statusName] = statusColor; // Renk bilgisini kaydet
      }
      methodGroups[methodId].statusCounts[statusName] += 1;
    });
    
    // Object'i array'e çevir ve sırala
    const sortedMethodStats = Object.values(methodGroups).sort((a, b) => b.totalAmount - a.totalAmount);
    setMethodStats(sortedMethodStats);
  };

  // Son 5 iadeyi getir
  const getLatestRefunds = () => {
    // Refunds bir array değilse veya boşsa, boş array döndür
    if (!Array.isArray(refunds) || refunds.length === 0) {
      console.log("Refunds verisi boş veya array değil:", refunds);
      return [];
    }
    
    return refunds.slice(0, 5);
  };

  // Durum rengi
  const getStatusClassForDashboard = (status, color) => {
    if (!color) {
      return getStatusClass(status);
    }
    return getStatusClass(status, color);
  };
  
  // Durum için style nesnesi
  const getStatusStyleForDashboard = (color) => {
    return getStatusStyle(color);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Hoş Geldiniz, {user?.full_name || user?.username}</h1>
        <p className="text-gray-600">İade işlemleri yönetim paneline hoş geldiniz. Aşağıda genel istatistikleri görebilirsiniz.</p>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Toplam İade */}
        <div className="bg-white rounded-lg shadow p-4 flex items-center">
          <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
            <RefreshCcw size={24} />
          </div>
          <div>
            <h3 className="text-gray-500 text-sm">Toplam İade</h3>
            <p className="text-2xl font-bold">{stats.totalRefunds}</p>
          </div>
        </div>

        {/* Toplam Tutar */}
        <div className="bg-white rounded-lg shadow p-4 flex items-center">
          <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
            <DollarSign size={24} />
          </div>
          <div>
            <h3 className="text-gray-500 text-sm">Toplam Tutar</h3>
            <p className="text-2xl font-bold">{stats.totalAmount} TL</p>
          </div>
        </div>
      </div>

      {/* Grafik ve Son İade İşlemleri */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Yöntemlere Göre İade Dağılımı */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-lg font-bold text-gray-800">Yöntemlere Göre İade Dağılımı</h2>
            <div className="flex items-center text-gray-500">
              <CreditCard size={20} className="mr-1" />
              <span className="text-sm">Ödeme Yöntemleri</span>
            </div>
          </div>

          <div className="p-4">
            {methodStats.length > 0 ? (
              <div className="overflow-hidden">
                {methodStats.map((method) => (
                  <div key={method.id} className="mb-4 pb-4 border-b border-gray-200 last:border-0 last:mb-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">{method.name}</h3>
                      <div className="text-right">
                        <span className="font-bold text-gray-900">{method.totalAmount.toFixed(2)} TL</span>
                        <span className="text-xs ml-2 text-gray-500">({method.totalCount} işlem)</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(method.statusCounts).map(([status, count]) => (
                        <span 
                          key={`${method.id}-${status}`} 
                          className={`text-xs px-2 py-1 rounded-full ${getStatusClassForDashboard(status, method.statusColors?.[status])}`}
                          style={getStatusStyleForDashboard(method.statusColors?.[status])}
                        >
                          {status}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <p>Henüz iade işlemi bulunmuyor.</p>
              </div>
            )}
          </div>
        </div>

        {/* Son İade İşlemleri */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-lg font-bold text-gray-800">Son İade İşlemleri</h2>
          </div>
          
          {getLatestRefunds().length > 0 ? (
            <div className="divide-y">
              {getLatestRefunds().map((refund) => (
                <div key={refund.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">{refund.member_name}</h3>
                      <p className="text-sm text-gray-500">{refund.payment_method_name} - {refund.reason}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{refund.amount} TL</p>
                      <span 
                        className={`text-xs px-2 py-1 rounded-full ${getStatusClassForDashboard(refund.status_name, refund.color)}`}
                        style={getStatusStyleForDashboard(refund.color)}
                      >
                        {refund.status_name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p>Henüz iade işlemi bulunmuyor.</p>
            </div>
          )}
          
          <div className="p-4 border-t bg-gray-50">
            <a href="/refunds" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
              Tüm İade İşlemlerini Görüntüle &rarr;
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;